// my-app/src/stores/authStore.ts
import { defineStore } from 'pinia';
import { login, register, initCaptcha } from '@/services/auth/authApi';
import type { LoginCredentials, RegisterCredentials } from '@/types/auth';
import { logger } from '@myorg/type-lib';

export interface UserInfo {
  userId: string
  merchantId: string
  name: string
  roles: string[]
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    // 記憶體中的絕對真實狀態
    isLoggedIn: false,
    user: null as UserInfo | null,
    
    // 獨立的訊息提示狀態，由 Facade 決定何時寫入、何時清空
    error: '',
    message: '',
  }),

  actions: {
    // 🛠️ 基礎 Setter
    setLoginStatus(status: boolean) {
      this.isLoggedIn = status;
    },

    setUser(user: UserInfo | null) {
      this.user = user;
    },

    setError(msg: string) {
      this.error = msg;
    },

    setMessage(msg: string) {
      this.message = msg;
    },

    // 🧹 職責單一：純粹重置使用者身分資料（不附帶訊息清除，保留最大彈性）
    clearAuthData() {
      this.isLoggedIn = false;
      this.user = null;
    },

    // 🧹 職責單一：純粹清除提示訊息
    clearMessages() {
      this.error = '';
      this.message = '';
    },

    // =========================================================================
    // ⚠️ 備註：以下 handle 函數屬於舊有帳密登入/註冊 API，正如 Review 所言，
    // 未來若全面轉向 OAuth BFF，此段可完全抽離至 accountStore 或是 api service 中。
    // =========================================================================
    async handleLogin(data: LoginCredentials & { rememberMe: boolean }) {
      this.clearMessages();
      logger.log("handleLogin data", data);
      try {
        const res = await login(data);
        this.message = '登入成功';
        return res;
      } catch (err: any) {
        this.error = err.response?.data?.message || '登入失敗';
        throw err;
      }
    },

    async handleRegister(data: RegisterCredentials) {
      this.clearMessages();
      try {
        await register(data);
        this.message = '註冊成功';
      } catch (err: any) {
        this.error = err.response?.data?.message || '註冊失敗';
        throw err;
      }
    },

    async initCaptcha() {
      try {
        return await initCaptcha();
      } catch (err) {
        this.error = '驗證碼初始化失敗';
        throw err;
      }
    }
  },
});